import { of, from, merge, EMPTY } from "rxjs";
import {
  switchMap,
  timeout,
  catchError,
  switchMapTo,
  throttleTime,
  filter,
} from "rxjs/operators";
import { ofType } from "redux-observable";
import appConfig from "@/appConfig";
import { sitemapUpdate } from "@dux/sitemap";
import { userLoaded } from "@dux/user";
import {
  localizationSessionUpdate,
  LOCALIZATION_SAVE_SUCCESS,
} from "@dux/localization";
import {
  buildDeployPendingDraftParams,
  buildCopyUserDraftParams,
  buildDiscardPendingDraftParams,
  getSessionError,
  parsePendingDraft,
} from "@domain/session";
import { notificationsAddSimple, notificationsInit } from "@dux/notifications";
import { getCurrentRole, resolveBinding } from "@domain/sitemap";
import { listCultures } from "@domain/localization";
import { ofHubEvent } from "@services/hub";
import { getBrowserLocale } from "@services/browser";
import {
  appLockEngage,
  APPLOCK_ACTION_ACTIVATED,
  appLockClear,
} from "@dux/appLock";
import hasValue from "@helper/hasValue";
import { hubConnect } from "@dux/hub";
import { ConfigurationErrorNoBinding } from "@errors/ConfigurationError";

export const SESSION_LOAD = "pss/session/LOAD";
export const SESSION_RETRY = "pss/session/RETRY";
export const SESSION_DESTROY = "pss/session/DESTROY";
export const SESSION_LOADING = "pss/session/LOADING";
export const SESSION_LOADED = "pss/session/LOADED";
export const SESSION_ERROR = "pss/session/ERROR";
export const SESSION_CHANGE_ROLE = "pss/session/CHANGE_ROLE";
export const SESSION_REQUEST_ROLE_REDIRECT =
  "pss/session/REQUEST_ROLE_REDIRECT";
export const SESSION_DENY_ROLE_REDIRECT = "pss/session/DENY_ROLE_REDIRECT";
export const SESSION_GRANT_ROLE_REDIRECT = "pss/session/GRANT_ROLE_REDIRECT";
export const SESSION_CHANGE_CULTURE = "pss/session/CHANGE_CULTURE";
export const SESSION_USER_INTERACTION = "pss/session/USER_INTERACTION";
export const SESSION_LOAD_PENDING_DRAFT = "pss/session/LOAD_PENDING_DRAFT";
export const SESSION_UPDATE_PENDING_DRAFT = "pss/session/UPDATE_PENDING_DRAFT";
export const SESSION_DEPLOY_PENDING_DRAFT = "pss/session/DEPLOY_PENDING_DRAFT";
export const SESSION_PENDING_DRAFT_DEPLOYED =
  "pss/session/PENDING_DRAFT_DEPLOYED";
export const SESSION_DISCARD_PENDING_DRAFT =
  "pss/session/DISCARD_PENDING_DRAFT";
export const SESSION_PENDING_DRAFT_DISCARDED =
  "pss/session/PENDING_DRAFT_DISCARDED";
export const SESSION_COPY_USER_DRAFT = "pss/session/COPY_USER_DRAFT";
export const SESSION_USER_DRAFT_COPIED = "pss/session/USER_DRAFT_COPIED";
export const SESSION_USER_DRAFT_SAVED = "pss/session/DRAFT_SAVED";
export const SESSION_SET_STATE = "pss/session/SET_STATE";
export const SESSION_CLEAR_INITIAL_REDIRECT =
  "pss/session/CLEAR_INITIAL_REDIRECT";
export const SESSION_TOGGLE_DARK_MODE = "pss/session/TOGGLE_DARK_MODE";

const LOCK_ID_USER_INACTIVITY = "UserInactivity";
const LOCK_ID_FORCED_RELOAD = "ForcedReload";

const PARAMS_STORAGE_KEY = "session-params";

const initialState = {
  loaded: false,
  lost: false,
  loading: false,
  error: null,
  activeUserId: null,
  currentRole: null,
  isAdministrator: false,
  canSignOut: false,
  canChangePassword: false,
  roles: [],
  roleRedirectRequest: null,
  currentCulture: null,
  cultures: [],
  inactivityTimeout: 0,
  deployment: null,
  deploymentId: null,
  canDeployPendingDraft: false,
  canCopyUserDraft: false,
  pendingDraftBusy: false,
  userDraftsBusy: false,
  pendingDraft: { locales: [], sitemaps: [], processes: [] },
  userDrafts: [],
  states: {},
  initialRedirectUrl: null,
  stylesheet: {},
  darkMode: -1,
  ...recoverSessionParams({}),
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SESSION_LOADING:
      return {
        ...initialState,
        // note: we must pass on the error to prevent flickering
        // whenever we're in the global session disconnected view
        error: state.error,
        loading: true,
      };
    case SESSION_LOADED:
      return {
        ...state,
        error: null,
        ...action.payload,
        loading: false,
        loaded: true,
      };
    case SESSION_REQUEST_ROLE_REDIRECT:
      return { ...state, roleRedirectRequest: action.payload };
    case SESSION_DENY_ROLE_REDIRECT:
      return { ...state, roleRedirectRequest: null };
    case SESSION_CHANGE_CULTURE:
      return {
        ...state,
        currentCulture: action.payload,
      };
    case SESSION_RETRY:
      return {
        ...state,
        loaded: !state.loaded && !state.error ? false : state.loaded,
        loading: false,
      };
    case SESSION_ERROR:
      return {
        ...initialState,
        lost: true,
        error: action.payload,
        loading: false,
      };
    case SESSION_UPDATE_PENDING_DRAFT:
      return {
        ...state,
        ...action.payload,
        canDeployPendingDraft:
          action.payload.pendingDraft && action.payload.pendingDraft.hasChanges,
        canCopyUserDraft: action.payload.userDrafts.length > 0,
        pendingDraftBusy: false,
        userDraftsBusy: false,
      };
    case SESSION_DEPLOY_PENDING_DRAFT:
    case SESSION_DISCARD_PENDING_DRAFT:
      return {
        ...state,
        pendingDraftBusy: true,
      };
    case SESSION_COPY_USER_DRAFT:
      return {
        ...state,
        userDraftsBusy: true,
      };
    case SESSION_PENDING_DRAFT_DEPLOYED:
    case SESSION_PENDING_DRAFT_DISCARDED:
      return { ...state, pendingDraftBusy: false };
    case SESSION_SET_STATE:
      return {
        ...state,
        states: { ...state.states, [action.payload.key]: action.payload.state },
      };
    case SESSION_CLEAR_INITIAL_REDIRECT:
      return {
        ...state,
        states: { ...state.states, initialRedirectUrl: null },
      };
    case SESSION_TOGGLE_DARK_MODE:
      return {
        ...state,
        darkMode: !state.darkMode,
      };
    default:
      return state;
  }
}

export function sessionLoad({ role = null, culture = null }) {
  return { type: SESSION_LOAD, payload: { role, culture } };
}

export function sessionRetry() {
  return { type: SESSION_RETRY };
}

export function sessionDestroy() {
  return { type: SESSION_DESTROY };
}

export function sessionChangeRole(role) {
  return { type: SESSION_CHANGE_ROLE, payload: role };
}

export function sessionRequestRoleRedirect(role, url) {
  return {
    type: SESSION_REQUEST_ROLE_REDIRECT,
    payload: { role, url },
  };
}

export function sessionRequestRoleBindingRedirect(
  role,
  binding,
  bindingContext
) {
  return {
    type: SESSION_REQUEST_ROLE_REDIRECT,
    payload: { role, binding, bindingContext },
  };
}

export function sessionDenyRoleRedirect() {
  return {
    type: SESSION_DENY_ROLE_REDIRECT,
  };
}

export function sessionGrantRoleRedirect() {
  return {
    type: SESSION_GRANT_ROLE_REDIRECT,
  };
}

export function sessionClearInitialRedirect() {
  return {
    type: SESSION_CLEAR_INITIAL_REDIRECT,
  };
}

export function sessionChangeCulture(culture) {
  return { type: SESSION_CHANGE_CULTURE, payload: culture };
}

export function sessionError(error) {
  return { type: SESSION_ERROR, payload: error };
}

export function sessionUserInteraction() {
  return { type: SESSION_USER_INTERACTION };
}

export function sessionDeployPendingDraft(data) {
  return { type: SESSION_DEPLOY_PENDING_DRAFT, payload: data };
}

export function sessionDiscardPendingDraft(data) {
  return { type: SESSION_DISCARD_PENDING_DRAFT, payload: data };
}

export function sessionCopyUserDraft(data) {
  return { type: SESSION_COPY_USER_DRAFT, payload: data };
}

export function sessionSetState(key, state) {
  return { type: SESSION_SET_STATE, payload: { key, state } };
}

export function sessionUserDraftSaved() {
  return { type: SESSION_USER_DRAFT_SAVED };
}

export function sessionToggleDarkMode() {
  return { type: SESSION_TOGGLE_DARK_MODE };
}

export function sessionLoading() {
  return { type: SESSION_LOADING };
}

function sessionLoaded(session) {
  return { type: SESSION_LOADED, payload: session };
}

function sessionUpdatePendingDraft(draft) {
  return {
    type: SESSION_UPDATE_PENDING_DRAFT,
    payload: draft,
  };
}

function sessionPendingDraftDeployed() {
  return {
    type: SESSION_PENDING_DRAFT_DEPLOYED,
  };
}

function sessionPendingDraftDiscarded() {
  return {
    type: SESSION_PENDING_DRAFT_DISCARDED,
  };
}

function sessionUserDraftCopied() {
  return { type: SESSION_USER_DRAFT_COPIED };
}

function recoverSessionParams(params) {
  let recovered = {};
  try {
    recovered = { ...JSON.parse(localStorage.getItem(PARAMS_STORAGE_KEY)) };
  } catch (e) {
    console.error(e);
  }
  return {
    role: params.role || recovered.role,
    culture: params.culture || recovered.culture || getBrowserLocale(),
    initialRedirect: recovered.initialRedirect || null,
    darkMode: params.darkMode || recovered.darkMode,
  };
}

function persistSessionParams(params) {
  localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify(params));
}

export function persistRole(role) {
  persistSessionParams(recoverSessionParams({ role }));
}

function sessionLoaded$(data, { culture, darkMode, initialRedirect }) {
  const {
    timeout,
    sitemap,
    userId,
    sessionModelUser = {},
    settings,
    roles,
    localization,
    isAdministrator,
    canSignOut,
    canChangePassword,
    deployment,
    notifications,
    stylesheet,
  } = data;

  const normCulture =
    listCultures(localization).find((c) => c === culture) ||
    listCultures(localization)[0];

  persistSessionParams({
    role: getCurrentRole(sitemap),
    culture: normCulture,
    darkMode: darkMode || -1,
  });

  let initialRedirectUrl = null;
  if (initialRedirect) {
    if (initialRedirect.url) {
      initialRedirectUrl = initialRedirect.url;
    } else {
      initialRedirectUrl = resolveBinding(
        sitemap,
        initialRedirect.binding,
        initialRedirect.bindingContext || {}
      );

      if (!initialRedirectUrl)
        return of(
          sessionError(
            ConfigurationErrorNoBinding({
              hint: initialRedirect.binding,
            })
          )
        );
    }
  }

  return of(
    userLoaded({ ...sessionModelUser, id: userId }, settings),
    sitemapUpdate(appConfig.sitemapOverride || sitemap),
    notificationsInit(appConfig.notificationsOverride || notifications),
    localizationSessionUpdate(localization),
    sessionLoaded({
      activeUserId: userId,
      inactivityTimeout: hasValue(appConfig.inactivityTimeoutOverride)
        ? appConfig.inactivityTimeoutOverride
        : timeout,
      currentRole: getCurrentRole(sitemap),
      roles,
      currentCulture: normCulture,
      cultures: listCultures(localization),
      isAdministrator: hasValue(appConfig.isAdministratorOverride)
        ? appConfig.isAdministratorOverride
        : isAdministrator,
      canSignOut,
      canChangePassword,
      deployment,
      initialRedirectUrl,
      stylesheet,
      darkMode,
    }),
    hubConnect()
  );
}

// TODO: There is a very nasty bug here that is probably caused by rxjs or fetch
// Sometimes, there is no result from the fetch, neither an error. The promise
// seems to be waiting forever and no code is executed to cleanup
export function loadSessionEpic(action$, store$, { api }) {
  return action$.pipe(
    ofType(SESSION_LOAD, SESSION_RETRY),
    switchMap(({ payload }) => {
      // Abort the processing whenever we're loaded fully without error
      // -> might be caused by a race condition between reducer and timeout to retry
      if (store$.value.session.loaded && !store$.value.session.error)
        return EMPTY;

      // Abort if we're already loading in parallel - should not happen, though
      if (store$.value.session.loading) return EMPTY;

      const { culture, role, initialRedirect, darkMode } = recoverSessionParams(
        payload || {}
      );
      const load$ = appConfig.sessionOverride
        ? sessionLoaded$(appConfig.sessionOverride, {
            culture,
            darkMode,
            initialRedirect: false,
          })
        : from(api.safeFetchGet("/Session/Load", { culture, role })).pipe(
            switchMap((result) => {
              if (result.error) return of(sessionError(result.error));
              const requestError = getSessionError(result.data);
              if (requestError) return of(sessionError(requestError));
              return sessionLoaded$(result.data, {
                culture,
                darkMode,
                initialRedirect,
              });
            })
          );

      return merge(of(sessionLoading()), load$);
    })
  );
}

export function loadPendingDraft(action$, store$, { api }) {
  return appConfig.sessionOverride
    ? EMPTY
    : action$.pipe(
        ofType(
          SESSION_LOADED,
          LOCALIZATION_SAVE_SUCCESS,
          SESSION_PENDING_DRAFT_DEPLOYED,
          SESSION_PENDING_DRAFT_DISCARDED,
          SESSION_USER_DRAFT_COPIED,
          SESSION_USER_DRAFT_SAVED
        ),
        switchMap(() => {
          const { isAdministrator, activeUserId } = store$.value.session;
          if (!isAdministrator) return EMPTY;
          return from(api.safeFetchGet("/Deployment/Drafts")).pipe(
            switchMap((result) => {
              if (result.error) return of(sessionError(result.error));
              return of(
                sessionUpdatePendingDraft(
                  parsePendingDraft(result.data, activeUserId)
                )
              );
            })
          );
        })
      );
}

export function deployPendingDraft(action$, store$, { api }) {
  return action$.pipe(
    ofType(SESSION_DEPLOY_PENDING_DRAFT),
    switchMap(({ payload }) => {
      const { deploymentId } = store$.value.session;
      return from(
        api.safeFetchPost(
          "/Deployment/Drafts",
          buildDeployPendingDraftParams({ ...payload, deploymentId })
        )
      ).pipe(
        switchMap((result) => {
          if (result.error) return of(sessionError(result.error));
          let notificationActions = [];
          switch (result.data?.message) {
            case "NothingToCommit":
              notificationActions = [
                notificationsAddSimple({
                  localKey: "::PSS::Notification::DeployDraft::NothingToCommit",
                }),
              ];
              break;
            case "BranchCreated":
              notificationActions = [
                notificationsAddSimple({
                  localKey: "::PSS::Notification::DeployDraft::BranchCreated",
                  localContext: { branch: result.data?.branch },
                }),
              ];
              break;
            default:
              break;
          }
          return of(sessionPendingDraftDeployed(), ...notificationActions);
        })
      );
    })
  );
}

export function discardPendingDraft(action$, store$, { api }) {
  return action$.pipe(
    ofType(SESSION_DISCARD_PENDING_DRAFT),
    switchMap(({ payload }) => {
      return from(
        api.safeFetchPost(
          "/Deployment/Drafts/Discard",
          buildDiscardPendingDraftParams({ ...payload })
        )
      ).pipe(
        switchMap((result) => {
          if (result.error) return of(sessionError(result.error));
          return of(sessionPendingDraftDiscarded());
        })
      );
    })
  );
}

export function copyUserDraft(action$, store$, { api }) {
  return action$.pipe(
    ofType(SESSION_COPY_USER_DRAFT),
    switchMap(({ payload }) => {
      return from(
        api.safeFetchPut(
          "/Deployment/Drafts",
          buildCopyUserDraftParams({ ...payload })
        )
      ).pipe(
        switchMap((result) => {
          if (result.error) return of(sessionError(result.error));
          return of(sessionUserDraftCopied());
        })
      );
    })
  );
}

export function updateDeploymentEpic(action$) {
  return action$.pipe(
    ofHubEvent("UpdateDeployment"),
    switchMap(() =>
      of(
        notificationsAddSimple({
          localKey: "::PSS::Notification::DeploymentUpdate",
        })
      )
    )
  );
}

export function destroySessionEpic(action$) {
  return action$.pipe(
    ofType(SESSION_DESTROY),
    switchMap(() => {
      window.location.replace(appConfig.logoutUrl);
      return EMPTY;
    })
  );
}

export function changeRoleEpic(action$, store$) {
  return action$.pipe(
    ofType(SESSION_CHANGE_ROLE),
    switchMap(({ payload }) => {
      const { currentRole, currentCulture, darkMode } = store$.value.session;
      if (payload !== currentRole) {
        persistSessionParams({
          role: payload,
          culture: currentCulture,
          darkMode,
        });
        window.location.replace("/");
      }

      return EMPTY;
    })
  );
}

export function grantRoleRedirectEpic(action$, store$) {
  return action$.pipe(
    ofType(SESSION_GRANT_ROLE_REDIRECT),
    switchMap(() => {
      const { roleRedirectRequest, currentCulture, darkMode } =
        store$.value.session;
      if (!roleRedirectRequest) return EMPTY;

      persistSessionParams({
        role: roleRedirectRequest.role,
        culture: currentCulture,
        initialRedirect: roleRedirectRequest,
        darkMode,
      });
      window.location.replace("/");
      return EMPTY;
    })
  );
}

export function changeCultureEpic(action$, store$) {
  return action$.pipe(
    ofType(SESSION_CHANGE_CULTURE, SESSION_TOGGLE_DARK_MODE),
    switchMap(({ payload }) => {
      const { currentRole, darkMode } = store$.value.session;
      persistSessionParams({ role: currentRole, culture: payload, darkMode });
      return EMPTY;
    })
  );
}

export function updateLocaleEpic(action$, store$, { api }) {
  return action$.pipe(
    ofHubEvent("UpdateLocale"),
    throttleTime(1000),
    switchMap(() => {
      const { currentRole, currentCulture } = store$.value.session;
      return from(
        api.safeFetchGet("/Session/Locale", {
          role: currentRole,
          culture: currentCulture,
        })
      ).pipe(
        switchMap((response) => {
          if (response.error) {
            return of(sessionError(response.error));
          }
          const { localization } = response.data;
          return of(localizationSessionUpdate(localization));
        })
      );
    })
  );
}

export function forceReloadEpic(action$, store$) {
  const lock$ = action$.pipe(
    ofHubEvent("ForceReload"),
    throttleTime(1000),
    switchMapTo(
      of(
        appLockEngage({
          id: LOCK_ID_FORCED_RELOAD,
          localKey: "::PSS::AppLock::ForcedReload",
          variant: "info",
          icon: "information-outline",
          actions: [
            {
              localKey: "Reload",
              icon: "sync",
            },
          ],
        })
      )
    )
  );

  const unlock$ = action$.pipe(
    ofType(APPLOCK_ACTION_ACTIVATED),
    filter(({ payload }) => payload.lockId === LOCK_ID_FORCED_RELOAD),
    switchMap(() => {
      const { currentCulture, currentRole } = store$.value.session;
      return of(
        appLockClear(LOCK_ID_FORCED_RELOAD),
        sessionLoad({ role: currentRole, culture: currentCulture })
      );
    })
  );

  return merge(lock$, unlock$);
}

export function inactivityAppLockEpic(action$, store$) {
  const lock$ = action$.pipe(
    ofType(SESSION_LOADED),
    switchMap(({ payload: { inactivityTimeout } }) => {
      if (!inactivityTimeout || inactivityTimeout < 1) return EMPTY;
      return action$.pipe(
        ofType(SESSION_USER_INTERACTION),
        timeout(inactivityTimeout),
        switchMapTo(EMPTY),
        catchError(() =>
          of(
            appLockEngage({
              id: LOCK_ID_USER_INACTIVITY,
              localKey: "::PSS::AppLock::UserInactivity",
              actions: [
                {
                  localKey: "Continue",
                  icon: "arrow-right-drop-circle",
                },
              ],
            })
          )
        )
      );
    })
  );

  const unlock$ = action$.pipe(
    ofType(APPLOCK_ACTION_ACTIVATED),
    filter(({ payload }) => payload.lockId === LOCK_ID_USER_INACTIVITY),
    switchMap(() => {
      const { currentCulture, currentRole } = store$.value.session;
      return of(
        appLockClear(LOCK_ID_USER_INACTIVITY),
        sessionLoad({ role: currentRole, culture: currentCulture })
      );
    })
  );

  return merge(lock$, unlock$);
}

export function notifyUserTaskAdded(action$, store$) {
  return action$.pipe(
    ofHubEvent("UserTaskAdded"),
    switchMap(({ payload }) => {
      if (
        !store$.value.link.activeProcessInstances.includes(
          payload.data?.processInstanceId
        )
      )
        return of(
          notificationsAddSimple({
            localKey: "::PSS::Notification::UserTaskAdded",
            contextData: payload.data || {},
            role: payload.data?.role,
            urlBinding: payload.data?.binding,
          })
        );
      return EMPTY;
    })
  );
}

export const sessionEpics = [
  loadSessionEpic,
  destroySessionEpic,
  changeRoleEpic,
  grantRoleRedirectEpic,
  changeCultureEpic,
  updateLocaleEpic,
  forceReloadEpic,
  inactivityAppLockEpic,
  loadPendingDraft,
  deployPendingDraft,
  discardPendingDraft,
  copyUserDraft,
  updateDeploymentEpic,
  notifyUserTaskAdded,
];
